import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import ProductVisual from "./ProductVisual";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} id="haut" data-testid="hero-section" className="luminous relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <MaskedLine delay={0.3}>
            <span className="text-sm uppercase tracking-[0.35em] text-gold">Maison du Bien-être</span>
          </MaskedLine>
          <h1 className="font-serif font-light text-choco leading-[0.95] mt-6 text-5xl sm:text-6xl lg:text-[5.75rem] tracking-tight">
            <MaskedLine delay={0.45}>KARYNA</MaskedLine>
          </h1>
          <div className="mt-8 font-accent italic text-xl sm:text-2xl text-choco/90">
            <MaskedLine delay={0.7}>Univers pour le Bien-être <span className="text-gold not-italic">✦</span> le Mouvement <span className="text-gold not-italic">✦</span></MaskedLine>
            <MaskedLine delay={0.85}>le Style <span className="text-gold not-italic">✦</span> le Voyage <span className="text-gold not-italic">✦</span> la Décoration</MaskedLine>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-10 max-w-md text-base font-light leading-relaxed text-choco"
          >
            Des objets choisis avec soin pour se sentir bien, bouger, voyager —
            et profiter pleinement de la vie.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mt-10"
          >
            <a
              data-testid="hero-discover-button"
              href="#univers"
              className="inline-block bg-choco text-ivory text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-denim transition-colors duration-500"
            >
              Découvrir l'univers
            </a>
          </motion.div>
        </div>
        <motion.div
          style={{ y }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-5 mt-4 md:mt-0"
        >
          <div className="spotlight aspect-[4/5] overflow-hidden relative">
            <span data-testid="hero-nouveaute-badge" className="absolute top-5 right-5 z-10 bg-gold text-ivory text-[10px] uppercase tracking-[0.25em] px-4 py-2">
              Nouveauté
            </span>
            <img
              src="/images/products/montre-connectee.jpg"
              alt="Montre Connectée KARYNA — cœur, sommeil et sport"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-choco/85 px-6 py-4">
              <p className="font-serif text-ivory text-lg font-light">Montre Connectée</p>
              <p className="text-ivory/70 text-xs font-light mt-1">La technologie rejoint l'univers KARYNA — pour elle & pour lui</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
